import { NavLink, useNavigate } from "react-router-dom";
import {
  Home,
  CheckSquare,
  ClipboardList,
  Calendar,
  Users,
  FileText,
  Bell,
  Building2,
  Eye,
  LayoutDashboard,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useAuth } from "@/app/providers/AuthProvider";
import { CAPABILITIES } from "@carmel/shared";

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** If set, the user must have this capability to see this item. */
  capability?: string;
}

const sidebarItems: NavItem[] = [
  { to: "/home", icon: Home, label: "Home" },
  { to: "/tasks", icon: CheckSquare, label: "Tasks" },
  { to: "/task-requests", icon: ClipboardList, label: "Requests" },
  { to: "/calendar", icon: Calendar, label: "Calendar" },
  { to: "/my-team", icon: Users, label: "Team" },
  { to: "/reports", icon: FileText, label: "Reports" },
  { to: "/notifications", icon: Bell, label: "Inbox" },
];

/** Admin/Superadmin: Oversight link */
const oversightItem: NavItem = {
  to: "/oversight",
  icon: Eye,
  label: "Oversight",
  capability: CAPABILITIES.DASHBOARD_VIEW_ORGANISATION,
};

/** Department heads: Department Dashboard link */
const departmentDashboardItem: NavItem = {
  to: "/department-dashboard",
  icon: LayoutDashboard,
  label: "Dept Dashboard",
  capability: CAPABILITIES.DASHBOARD_VIEW_DEPARTMENT,
};

/** Admin/Superadmin: Organisation admin link */
const adminItem: NavItem = {
  to: "/admin",
  icon: Building2,
  label: "Organisation",
  capability: CAPABILITIES.ORGANISATION_MANAGE,
};

export function DesktopSidebar() {
  const { hasCapability } = useCapabilities();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  // Filter sidebar items by capabilities
  const visibleItems = sidebarItems.filter(
    (item) => !item.capability || hasCapability(item.capability as never),
  );

  // Build role-aware additional items
  const additionalItems: NavItem[] = [];
  if (hasCapability(CAPABILITIES.DASHBOARD_VIEW_ORGANISATION as never)) {
    additionalItems.push(oversightItem);
  }
  if (hasCapability(CAPABILITIES.DASHBOARD_VIEW_DEPARTMENT as never)) {
    additionalItems.push(departmentDashboardItem);
  }
  if (hasCapability(CAPABILITIES.ORGANISATION_MANAGE as never)) {
    additionalItems.push(adminItem);
  }

  return (
    <aside className="hidden h-screen w-[var(--sidebar-width)] flex-col border-r border-[var(--border)] bg-[var(--card)] md:flex" aria-label="Sidebar navigation">
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 border-b border-[var(--border)] px-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#a855f7] to-[#7c3aed]">
          <img
            src="/carmel-logo-white.png"
            alt="Carmel Work logo"
            className="h-5 w-5 object-contain"
            width={20}
            height={20}
          />
        </div>
        <span className="text-base font-semibold text-[var(--foreground)]">
          Carmel Work
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-3" aria-label="Main navigation">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-sm font-medium transition-all duration-150",
                isActive
                  ? "bg-[var(--accent)] text-[var(--accent-foreground)] dark:bg-[var(--accent)] dark:text-[var(--accent-foreground)]"
                  : "text-[var(--color-body)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] dark:text-[var(--muted-foreground)] dark:hover:text-[var(--foreground)]",
              )
            }
          >
            <item.icon className="h-5 w-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}

        {/* Role-aware additional items */}
        {additionalItems.length > 0 && (
          <>
            <div className="my-2 border-t border-[var(--border)]" />
            {additionalItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-sm font-medium transition-all duration-150",
                    isActive
                      ? "bg-[var(--accent)] text-[var(--accent-foreground)] dark:bg-[var(--accent)] dark:text-[var(--accent-foreground)]"
                      : "text-[var(--color-body)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] dark:text-[var(--muted-foreground)] dark:hover:text-[var(--foreground)]",
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-[var(--border)] p-3">
        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-left text-sm font-medium text-[#b83280] transition-all duration-150 hover:bg-[var(--muted)] hover:text-[#9d2870] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card)] dark:text-[#f08ac7] dark:hover:text-[#f5b3dc]"
        >
          <LogOut className="h-5 w-5" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}
